import React, { useEffect, useState } from 'react';
import { Camera } from 'lucide-react';
import { usePage } from '../../hooks/usePage';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../../hooks/useToast';
import { authApi } from '../../api/auth';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';

const fileToBase64 = (file: File) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });

export const Settings: React.FC = () => {
  usePage('Settings');
  const { admin, setCurrentAdmin } = useAuth();
  const toast = useToast();

  const [profileForm, setProfileForm] = useState({
    firstName: admin?.firstName ?? '',
    lastName: admin?.lastName ?? '',
  });
  const [photoPreview, setPhotoPreview] = useState<string | null>(
    admin?.profilePhoto ?? null,
  );
  const [profileLoading, setProfileLoading] = useState(false);
  const [photoLoading, setPhotoLoading] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  useEffect(() => {
    if (!admin) return;
    setProfileForm({
      firstName: admin.firstName,
      lastName: admin.lastName,
    });
    setPhotoPreview(admin.profilePhoto);
  }, [admin]);

  if (!admin) return null;

  async function saveProfile() {
    if (!profileForm.firstName.trim() || !profileForm.lastName.trim()) {
      toast.warning('First name and last name are required.');
      return;
    }
    try {
      setProfileLoading(true);
      const nextAdmin = await authApi.updateProfile({
        firstName: profileForm.firstName.trim(),
        lastName: profileForm.lastName.trim(),
      });
      setCurrentAdmin(nextAdmin);
      toast.success('Profile updated.');
    } catch (error) {
      toast.error(error, 'Could not update profile.');
    } finally {
      setProfileLoading(false);
    }
  }

  async function handlePhotoChange(event: React.ChangeEvent<HTMLInputElement>) {
    if (!admin) return;
    const file = event.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.warning('Please choose an image file.');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.warning('Profile picture must be under 5MB.');
      return;
    }

    const previousPhoto = admin.profilePhoto;
    try {
      setPhotoLoading(true);
      const base64 = await fileToBase64(file);
      setPhotoPreview(base64);
      const nextAdmin = await authApi.updateProfilePhoto(base64);
      setCurrentAdmin(nextAdmin);
      setPhotoPreview(nextAdmin.profilePhoto);
      toast.success('Profile picture updated.');
    } catch (error) {
      setPhotoPreview(previousPhoto);
      toast.error(error, 'Could not update profile picture.');
    } finally {
      setPhotoLoading(false);
      event.target.value = '';
    }
  }

  async function changePassword() {
    if (!passwordForm.currentPassword || !passwordForm.newPassword) {
      toast.warning('Enter your current and new password.');
      return;
    }
    if (passwordForm.newPassword.length < 8) {
      toast.warning('New password must be at least 8 characters.');
      return;
    }
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast.warning('Passwords do not match.');
      return;
    }

    try {
      setPasswordLoading(true);
      await authApi.changePassword(
        passwordForm.currentPassword,
        passwordForm.newPassword,
      );
      setPasswordForm({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
      toast.success('Password changed. Please sign in again if prompted.');
    } catch (error) {
      toast.error(error, 'Could not change password.');
    } finally {
      setPasswordLoading(false);
    }
  }

  const initials = `${admin.firstName[0] ?? ''}${admin.lastName[0] ?? ''}`.toUpperCase();

  return (
    <div style={{ display: 'grid', gap: 20, maxWidth: 900 }}>
      <Card padding={24}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 18, marginBottom: 24 }}>
          <div
            style={{
              width: 84,
              height: 84,
              borderRadius: '50%',
              background: 'var(--blue-100)',
              color: 'var(--blue-700)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 24,
              fontWeight: 800,
              overflow: 'hidden',
              border: '1px solid var(--neutral-300)',
            }}
          >
            {photoPreview ? (
              <img
                src={photoPreview}
                alt=""
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            ) : (
              initials
            )}
          </div>
          <div>
            <h2 style={{ margin: 0, fontSize: 18, color: 'var(--neutral-800)' }}>
              Profile picture
            </h2>
            <p style={{ margin: '4px 0 12px', fontSize: 13, color: 'var(--neutral-500)' }}>
              This image is shown in support chats when you reply to customers.
            </p>
            <label>
              <input
                type="file"
                accept="image/*"
                onChange={handlePhotoChange}
                style={{ display: 'none' }}
                disabled={photoLoading}
              />
              <span
                style={{
                  display: 'inline-flex',
                  height: 36,
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 8,
                  padding: '0 14px',
                  borderRadius: 'var(--radius-md)',
                  border: '1px solid var(--neutral-300)',
                  color: 'var(--neutral-800)',
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: photoLoading ? 'not-allowed' : 'pointer',
                  opacity: photoLoading ? 0.65 : 1,
                }}
              >
                <Camera size={15} />
                {photoLoading ? 'Uploading...' : 'Change photo'}
              </span>
            </label>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 220px), 1fr))', gap: 14 }}>
          <Input
            label="First name"
            value={profileForm.firstName}
            onChange={(event) =>
              setProfileForm((state) => ({ ...state, firstName: event.target.value }))
            }
          />
          <Input
            label="Last name"
            value={profileForm.lastName}
            onChange={(event) =>
              setProfileForm((state) => ({ ...state, lastName: event.target.value }))
            }
          />
        </div>
        <div style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end' }}>
          <Button onClick={() => void saveProfile()} loading={profileLoading}>
            Save profile
          </Button>
        </div>
      </Card>

      <Card padding={24}>
        <h2 style={{ margin: '0 0 16px', fontSize: 18, color: 'var(--neutral-800)' }}>
          Change password
        </h2>
        <div style={{ display: 'grid', gap: 14, maxWidth: 480 }}>
          <Input
            label="Current password"
            type="password"
            value={passwordForm.currentPassword}
            onChange={(event) =>
              setPasswordForm((state) => ({
                ...state,
                currentPassword: event.target.value,
              }))
            }
          />
          <Input
            label="New password"
            type="password"
            value={passwordForm.newPassword}
            onChange={(event) =>
              setPasswordForm((state) => ({
                ...state,
                newPassword: event.target.value,
              }))
            }
          />
          <Input
            label="Confirm new password"
            type="password"
            value={passwordForm.confirmPassword}
            onChange={(event) =>
              setPasswordForm((state) => ({
                ...state,
                confirmPassword: event.target.value,
              }))
            }
          />
        </div>
        <div style={{ marginTop: 16 }}>
          <Button onClick={() => void changePassword()} loading={passwordLoading}>
            Change password
          </Button>
        </div>
      </Card>
    </div>
  );
};
